import React from 'react';
import { ExternalLink, Calendar, Sparkles } from 'lucide-react';

const LumaBanner = ({ eventUrl, title, badge, description, eventDate }) => {
    const [timeLeft, setTimeLeft] = React.useState(null);

    React.useEffect(() => {
        if (!eventDate) {
            setTimeLeft(null);
            return;
        }

        const calculateTimeLeft = () => {
            // Ensure eventDate is treated as a date object
            const difference = +new Date(eventDate) - +new Date();
            let timeLeft = {};

            if (difference > 0) {
                timeLeft = {
                    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                    minutes: Math.floor((difference / 1000 / 60) % 60),
                    seconds: Math.floor((difference / 1000) % 60),
                };
            } else {
                timeLeft = null;
            }
            return timeLeft;
        };

        // Initial set
        const initialTime = calculateTimeLeft();
        if (initialTime) {
            setTimeLeft(initialTime);
        }

        const timer = setInterval(() => {
            const t = calculateTimeLeft();
            setTimeLeft(t);
            if (!t) clearInterval(timer);
        }, 1000);

        return () => clearInterval(timer);
    }, [eventDate]);

    if (!eventUrl) return null;

    const displayTitle = title || "Upcoming Club Event";
    const displayBadge = badge || "featured";
    const displayDescription = description || "Click to view details and register on Luma";

    return (
        <div className="w-full">
            <a
                href={eventUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative flex flex-col sm:flex-row items-center justify-between w-full overflow-hidden rounded-xl bg-white shadow-sm hover:shadow-md transform hover:-translate-y-0.5 transition-all duration-300 ring-1 ring-gray-100 p-4 gap-4"
                style={{ textDecoration: 'none' }}
            >
                {/* Gradient Background Effect with Movement */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 animate-[gradient-x_3s_ease-in-out_infinite]"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 via-white to-purple-50/50 opacity-100 group-hover:opacity-90 transition-opacity"></div>

                {/* Content Container - Flex Row */}
                <div className="relative z-10 flex flex-1 items-center gap-4 w-full">

                    {/* Icon Box */}
                    <div className="relative flex-shrink-0 flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-gray-100 text-indigo-600 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                        <div className="animate-[wiggle_3s_ease-in-out_infinite]">
                            <Calendar size={24} strokeWidth={2} />
                        </div>
                    </div>

                    {/* Text Info */}
                    <div className="flex flex-col min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="relative inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-bold text-indigo-700 uppercase tracking-wide">
                                <Sparkles size={10} className="animate-spin-slow" />
                                {displayBadge}
                            </span>
                        </div>

                        <h3 className="text-base font-bold text-gray-900 leading-tight group-hover:text-indigo-900 transition-colors truncate">
                            {displayTitle}
                        </h3>

                        {/* Description (Visible if no timer or on very small screens) */}
                        {!timeLeft && (
                            <p className="text-xs text-gray-500 font-medium truncate mt-0.5">
                                {displayDescription}
                            </p>
                        )}
                    </div>
                </div>

                {/* Timer & Action Container */}
                <div className="relative z-10 flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto mt-2 sm:mt-0">
                    {/* Countdown Timer */}
                    {timeLeft && (
                        <div className="flex justify-center gap-2">
                            {[
                                { label: 'Days', value: timeLeft.days },
                                { label: 'Hrs', value: timeLeft.hours },
                                { label: 'Mins', value: timeLeft.minutes },
                                { label: 'Secs', value: timeLeft.seconds },
                            ].map((item, idx) => (
                                <div key={idx} className="flex flex-col items-center bg-gray-50/80 backdrop-blur-sm rounded-lg p-1.5 min-w-[2.5rem] border border-gray-100 shadow-sm relative overflow-hidden group/timer">
                                    <span className="text-sm font-bold text-indigo-600 font-mono tabular-nums tracking-tight z-10">
                                        {item.value.toString().padStart(2, '0')}
                                    </span>
                                    <span className="text-[8px] uppercase tracking-wider text-gray-400 font-semibold z-10">
                                        {item.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Action Button */}
                    <span className="flex-shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-900 text-white text-xs font-bold rounded-lg shadow-sm group-hover:bg-indigo-600 group-hover:shadow-indigo-500/25 transition-all duration-300 w-full sm:w-auto">
                        Register Now
                        <ExternalLink size={14} strokeWidth={2.5} className="group-hover:translate-x-0.5 transition-transform" />
                    </span>
                </div>

            </a>

            <style>{`
                @keyframes wiggle {
                    0%, 100% { transform: rotate(-3deg); }
                    50% { transform: rotate(3deg); }
                }
                @keyframes spin-slow {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .animate-spin-slow {
                    animation: spin-slow 8s linear infinite;
                }
            `}</style>
        </div>
    );
};

export default LumaBanner;
